/**
 * Transporte de la telemetría 3.1 a L5FrontEvent (`POST {REACT_APP_EVENTS_API}public/front/event/v3`).
 *
 * - Lote: se envía cada 5 s o en cuanto hay 10 eventos, con
 *   `{"sent_at": …, "events": [...]}` y como mucho 50 eventos por petición
 *   (límite del contrato). `sent_at` es la hora del navegador al serializar
 *   ESE envío (también al reintentar la cola guardada): L5FrontEvent corrige
 *   la hora de cada evento con received_at - sent_at.
 * - Al salir de la página (o al ocultarse la pestaña) lo pendiente sale con
 *   `sendBeacon`. El cuerpo va como texto (`text/plain`): así es una petición
 *   simple, sin preflight CORS; L5FrontEvent lo lee como JSON igualmente.
 * - Lo que no se pudo enviar (red caída, 5xx, 429) se guarda en una cola en
 *   localStorage con tope de tamaño y se reintenta con espera creciente; al
 *   cargar la página se reintenta lo que quedó de la visita anterior. Un 4xx
 *   (lote que no cumple el contrato) no se reintenta: fallaría siempre.
 */
import { TrackedEvent } from "./events";

export const QUEUE_KEY = "__l5_tracker_v3_queue";
/** Cola de la telemetría V2, retirada: se borra al arrancar. */
export const LEGACY_QUEUE_KEY = "__l5_tracker_queue";

type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;
type SendResult = "ok" | "retry" | "drop";

export interface TransportConfig {
  endpoint: string | null;
  /** Eventos en memoria que disparan un envío inmediato. */
  maxBatch?: number;
  flushIntervalMs?: number;
  /** Máximo de eventos por petición (el contrato admite 50). */
  maxPerRequest?: number;
  /** Máximo de eventos guardados para reintentar (se quedan los más recientes). */
  maxQueue?: number;
  retryBaseMs?: number;
  retryMaxMs?: number;
  fetchFn?: typeof fetch;
  beacon?: (url: string, body: string) => boolean;
  storage?: StorageLike | null;
  /** Sin endpoint (local), los eventos solo se escriben aquí. */
  log?: (event: TrackedEvent) => void;
  now?: () => number;
}

export const eventsEndpoint = (
  base: string | undefined = process.env.REACT_APP_EVENTS_API
): string | null => {
  const trimmed = (base ?? "").trim();
  if (!trimmed) return null;
  return `${trimmed.endsWith("/") ? trimmed : `${trimmed}/`}public/front/event/v3`;
};

const defaultStorage = (): StorageLike | null => {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch {
    return null;
  }
};

const defaultBeacon = (url: string, body: string): boolean => {
  try {
    if (typeof navigator === "undefined" || !navigator.sendBeacon) return false;
    return navigator.sendBeacon(url, body);
  } catch {
    return false;
  }
};

/** Cuerpo de un envío: la hora de ahora, no la de creación de los eventos. */
export const batchBody = (events: TrackedEvent[], now: number): string =>
  JSON.stringify({ sent_at: new Date(now).toISOString(), events });

const chunk = <T,>(items: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
};

export class TrackingTransport {
  private readonly endpoint: string | null;
  private readonly maxBatch: number;
  private readonly flushIntervalMs: number;
  private readonly maxPerRequest: number;
  private readonly maxQueue: number;
  private readonly retryBaseMs: number;
  private readonly retryMaxMs: number;
  private readonly fetchFn: typeof fetch | null;
  private readonly beacon: (url: string, body: string) => boolean;
  private readonly storage: StorageLike | null;
  private readonly log?: (event: TrackedEvent) => void;
  private readonly now: () => number;

  private buffer: TrackedEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private retryTimer: ReturnType<typeof setTimeout> | null = null;
  private retryDelay: number;

  constructor(config: TransportConfig) {
    this.endpoint = config.endpoint;
    this.maxBatch = config.maxBatch ?? 10;
    this.flushIntervalMs = config.flushIntervalMs ?? 5000;
    this.maxPerRequest = Math.min(config.maxPerRequest ?? 50, 50);
    this.maxQueue = config.maxQueue ?? 200;
    this.retryBaseMs = config.retryBaseMs ?? 5000;
    this.retryMaxMs = config.retryMaxMs ?? 60000;
    this.fetchFn =
      config.fetchFn ?? (typeof fetch === "function" ? fetch.bind(globalThis) : null);
    this.beacon = config.beacon ?? defaultBeacon;
    this.storage = config.storage === undefined ? defaultStorage() : config.storage;
    this.log = config.log;
    this.now = config.now ?? (() => Date.now());
    this.retryDelay = this.retryBaseMs;
  }

  enqueue(event: TrackedEvent): void {
    if (!this.endpoint) {
      this.log?.(event);
      return;
    }
    this.buffer.push(event);
    if (this.buffer.length >= this.maxBatch) {
      void this.flush();
      return;
    }
    if (!this.flushTimer) {
      this.flushTimer = setTimeout(() => void this.flush(), this.flushIntervalMs);
    }
  }

  /** Envía lo que hay en memoria. Lo que falla queda en la cola persistente. */
  async flush(): Promise<void> {
    this.clearFlushTimer();
    if (!this.endpoint || this.buffer.length === 0) return;
    const pending = this.buffer;
    this.buffer = [];
    await Promise.all(
      chunk(pending, this.maxPerRequest).map(async (batch) => {
        if ((await this.send(batch)) === "retry") {
          this.persist(batch);
          this.scheduleRetry();
        }
      })
    );
  }

  /**
   * Al salir: lo que hay en memoria sale con `sendBeacon` (síncrono). Si el
   * navegador no lo acepta, se guarda para la próxima visita. La cola de
   * reintentos no se manda aquí: si el servidor estaba caído, perderla en un
   * beacon sin respuesta sería peor que esperar a la siguiente carga.
   */
  flushOnExit(): void {
    this.clearFlushTimer();
    if (!this.endpoint || this.buffer.length === 0) return;
    const pending = this.buffer;
    this.buffer = [];
    chunk(pending, this.maxPerRequest).forEach((batch) => {
      if (!this.beacon(this.endpoint as string, batchBody(batch, this.now()))) {
        this.persist(batch);
      }
    });
  }

  /** Al arrancar: reintenta lo que quedó guardado de visitas anteriores. */
  resume(): void {
    this.removeKey(LEGACY_QUEUE_KEY);
    if (!this.endpoint) return;
    if (this.readQueue().length > 0) void this.retryPersisted();
  }

  /** Eventos guardados para reintentar (para tests y diagnóstico). */
  persistedEvents(): TrackedEvent[] {
    return this.readQueue();
  }

  bufferedCount(): number {
    return this.buffer.length;
  }

  private async retryPersisted(): Promise<void> {
    this.retryTimer = null;
    const queued = this.readQueue();
    if (queued.length === 0) return;
    this.removeKey(QUEUE_KEY);
    let failed = false;
    for (const batch of chunk(queued, this.maxPerRequest)) {
      if ((await this.send(batch)) === "retry") {
        this.persist(batch);
        failed = true;
      }
    }
    if (failed) {
      this.retryDelay = Math.min(this.retryDelay * 2, this.retryMaxMs);
      this.scheduleRetry();
    } else {
      this.retryDelay = this.retryBaseMs;
    }
  }

  private scheduleRetry(): void {
    if (this.retryTimer) return;
    this.retryTimer = setTimeout(() => void this.retryPersisted(), this.retryDelay);
  }

  private async send(batch: TrackedEvent[]): Promise<SendResult> {
    if (!this.endpoint || !this.fetchFn) return "retry";
    try {
      const response = await this.fetchFn(this.endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: batchBody(batch, this.now()),
        keepalive: true,
        credentials: "omit",
      });
      if (response.ok) return "ok";
      if (response.status === 408 || response.status === 429 || response.status >= 500) {
        return "retry";
      }
      return "drop";
    } catch {
      return "retry";
    }
  }

  private persist(events: TrackedEvent[]): void {
    if (!this.storage) return;
    const seen = new Set<string>();
    const merged = this.readQueue()
      .concat(events)
      .filter((e) => {
        if (!e || seen.has(e.client_event_id)) return false;
        seen.add(e.client_event_id);
        return true;
      });
    try {
      this.storage.setItem(QUEUE_KEY, JSON.stringify(merged.slice(-this.maxQueue)));
    } catch {
      /* localStorage lleno o bloqueado: se pierden, no rompen la app */
    }
  }

  private readQueue(): TrackedEvent[] {
    if (!this.storage) return [];
    try {
      const raw = this.storage.getItem(QUEUE_KEY);
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  private removeKey(key: string): void {
    try {
      this.storage?.removeItem(key);
    } catch {
      /* noop */
    }
  }

  private clearFlushTimer(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}
